import { useState } from 'react';
import { Button, Chip, Stack, TextField, MenuItem, FormControlLabel, Switch, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton } from '@mui/material';
import { CheckCircle, Cancel, FastForward, Payments } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { amPaymentsApi, type AmPaymentRequest, type ActPaymentInput, STATUS_LABEL } from '../../api/amPayments.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const color = (s: string): 'success' | 'error' | 'warning' | 'info' | 'default' =>
  s === 'paid' ? 'success' : s === 'rejected' ? 'error' : s === 'pending_finance' ? 'info' : 'warning';

export function AmPaymentsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const roles = (user?.roles ?? []) as string[];
  const isClient = user?.linkedEntityType === 'client';
  const isAdmin = roles.includes('super_admin') || roles.includes('admin');
  const isBH = roles.includes('business_head') || isAdmin;
  const isFinance = roles.includes('finance') || isAdmin;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(true);
  const [payRow, setPayRow] = useState<AmPaymentRequest | null>(null);
  const [payForm, setPayForm] = useState<ActPaymentInput>({ action: 'pay', paymentMode: 'NEFT' });
  const [rejectRow, setRejectRow] = useState<AmPaymentRequest | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['am-payments', page, limit, status, mine],
    queryFn: () => amPaymentsApi.list({ page, limit, status: status || undefined, queue: mine ? 'mine' : 'all' }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['am-payments'] });

  const actMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ActPaymentInput }) => amPaymentsApi.act(id, body),
    onSuccess: () => { setPayRow(null); setRejectRow(null); setReason(''); invalidate(); },
    onError: (e) => { setError(extractApiError(e)); setToast(extractApiError(e)); },
  });
  const act = (id: number, body: ActPaymentInput) => actMut.mutate({ id, body });

  // Which actions can the current user take on a row at its stage?
  const canApprove = (r: AmPaymentRequest) =>
    (isClient && (r.status === 'pending_client_ops' || r.status === 'pending_client_finance')) ||
    (isBH && r.status === 'pending_business_head');
  const canOverride = (r: AmPaymentRequest) => isBH && ['pending_client_ops', 'pending_client_finance', 'pending_business_head'].includes(r.status);
  const canPay = (r: AmPaymentRequest) => isFinance && r.status === 'pending_finance';
  const canReject = (r: AmPaymentRequest) => canApprove(r) || canPay(r);

  const columns: Column<AmPaymentRequest>[] = [
    { key: 'requestCode', label: 'Request', width: 130 },
    { key: 'entityType', label: 'For', render: (r) => r.entityType },
    { key: 'stationName', label: 'Station', render: (r) => <span>{r.stationName ?? '—'}<br /><small style={{ color: '#888' }}>{r.clientName ?? ''}</small></span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'status', label: 'Stage', render: (r) => <Chip size="small" color={color(r.status)} label={STATUS_LABEL[r.status]} /> },
  ];

  return (
    <>
      <PageHeader title="Payment Approvals" subtitle="Client Ops → Client Finance → Business Head → Finance." />
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField select size="small" label="Stage" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={mine} onChange={(e) => { setMine(e.target.checked); setPage(1); }} />} label="Awaiting my action" />
      </Stack>

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {canApprove(r) && <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => act(r.id, { action: 'approve' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
            {canPay(r) && <Tooltip title="Record payment"><IconButton size="small" color="primary" onClick={() => { setError(null); setPayForm({ action: 'pay', paymentMode: 'NEFT' }); setPayRow(r); }}><Payments fontSize="small" /></IconButton></Tooltip>}
            {canOverride(r) && <Tooltip title="Emergency override → Finance"><IconButton size="small" color="warning" onClick={() => act(r.id, { action: 'override' })}><FastForward fontSize="small" /></IconButton></Tooltip>}
            {canReject(r) && <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => { setError(null); setReason(''); setRejectRow(r); }}><Cancel fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      {/* Finance — record payment */}
      <Dialog open={Boolean(payRow)} onClose={() => setPayRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {payRow && <Alert severity="info">{payRow.requestCode} — {inr(payRow.amount)} ({payRow.entityType})</Alert>}
            <TextField select label="Payment Mode" size="small" value={payForm.paymentMode} onChange={(e) => setPayForm((f) => ({ ...f, paymentMode: e.target.value as ActPaymentInput['paymentMode'] }))}>
              {['NEFT', 'RTGS', 'UPI', 'Cheque', 'Cash'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Transaction Ref" size="small" value={payForm.transactionRef ?? ''} onChange={(e) => setPayForm((f) => ({ ...f, transactionRef: e.target.value }))} />
            <TextField label="Paid Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={payForm.paidDate ?? ''} onChange={(e) => setPayForm((f) => ({ ...f, paidDate: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayRow(null)}>Cancel</Button>
          <Button variant="contained" disabled={actMut.isPending} onClick={() => payRow && act(payRow.id, { ...payForm, action: 'pay' })}>Confirm Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Reject */}
      <Dialog open={Boolean(rejectRow)} onClose={() => setRejectRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject {rejectRow?.requestCode}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Reason" size="small" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectRow(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={actMut.isPending} onClick={() => rejectRow && act(rejectRow.id, { action: 'reject', reason: reason || undefined })}>Reject</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
